import { Injectable } from "@angular/core";
import { createStore, withProps } from "@ngneat/elf";
import { localStorageStrategy, persistState } from "@ngneat/elf-persist-state";
import { elfMigration } from "./elf-migration";
import { debounceTime } from "rxjs";
import { RideOpsSettingsWithDeviceSettings } from "./location-state.service";

const locationStateStore = createStore(
	{ name: "location-state" },
	withProps<LocationState>({
		state: "Closed",
		createdDowntimesAreBlocking: undefined,
		downtime: undefined,
		apiKey: undefined,
		location: undefined,
		operator: undefined,
		isClearedForOperationDetails: {
			preopeningChecklists: [],
			checklists: [],
			assignments: [],
			noteHandoversRequiringAcknowledgement: [],
			isClearedForPreopening: false,
			isClearedForOperation: false,
			hasBlockingDowntime: false,
		},
		locationProperties: [],
		operators: [],
		rideOpsSettings: {
			dispatchUnitCapacity: 1,
			dispatchUnits: 1,
			useAssetBasedDispatchConfiguration: false,
			units: [],
			allowStatisticsForOperator: false,
			dispatchUpdateWarningThreshold: 15,
			queueTimeUpdateWarningThreshold: 15,
			maximumQueueTimeMinutes: 60,
			queueTimeStepSizeMinutes: 5,
			lockDeviceSettings: false,
			minumumSecondsBetweenDispatches: 0,
			messageForOperatorWhenNotReadyForOperation: "",
			messageForOperatorWhenNoCriticalForOperationChecklistsPlanned: "",
			positions: [],
			attendants: [],
			signoutAllAttendantsOnDowntimeCreation: false,
			deviceSettings: {
				allowChangingDispatchesWhenClosingLocation: true,
				allowChangingRidersWhenClosingLocation: true,
				showQueueTimeWidget: true,
				showDispatchWidget: true,
				maximumRidersPerDispatch: 900,
				dispatchWidgetType: "selector",
				dispatchGridNumberDirection: "ascending",
				dispatchMetric: "occupiedSeats",
				showSafariAiDispatchWidget: false,
				showSafariAiQueueTimeWidget: false,
				showBulletinWidget: false,
			},
			externalDataSettings: {
				safariAiEnabled: false,
			},
			bulletinWidgetSettings: {
				bulletinWidgetEnabled: false,
				title: "",
				content: "",
			},
			delayedDispatches: false,
			emptyDispatches: false,
			closingTimeReminderEnabled: false,
			allowRideOpsOperatorToResolveNonBlockingDowntime: false,
		},
		requiredCompetencies: [],
		requiredCompetenciesForAttendants: [],
		preopeningChecklist: undefined,
		closingChecklist: undefined,
		noteTypes: [],
		noteTemplates: [],
		downtimeTemplates: [],
		lastServerUpdate: null,
		lastUpdateFailed: false,
		lastQueueUpdate: undefined,
		lastRidersUpdate: undefined,
		riders: 0,
		dispatches: 0,
		assetDispatchQueueIndex: 0,
		lastAssetDispatchOccupiedSeats: undefined,
		lastAssetDispatchCountByUnitId: {},
		locationOpenSessionStart: undefined,
		dispatchCooldownUntil: undefined,
		assetUnitOrderByLocationId: {},
		lastExternalDispatch: undefined,
		lastExternalQueueTime: undefined,
		organizationDateTimeSettings: undefined,
	}),
);

@Injectable({ providedIn: "root" })
export class LocationStateStore {
	constructor() {
		// Elf Migration from Akita to Elf is done here, because LocationStateStore is constructed very early
		// I wanted to do this in the App Initializer, but this is constructed even before.
		// Migrating here, makes sure data is ready in localStorage, before persistState tries to load it
		elfMigration();
		persistState(locationStateStore, {
			storage: localStorageStrategy,
			source: () => locationStateStore.pipe(debounceTime(1000)),
		});
	}

	public store = locationStateStore;

	reset = () => {
		locationStateStore.reset();
	};
}

export interface LocationState {
	state: "Open" | "Closed";
	createdDowntimesAreBlocking: boolean;
	downtime: Downtime;
	apiKey: string | null;
	location?: Location;
	operator: CurrentOperator;
	operators: Operator[];
	rideOpsSettings: RideOpsSettingsWithDeviceSettings;
	requiredCompetencies: DocumentReference[];
	requiredCompetenciesForAttendants: DocumentReference[];
	preopeningChecklist: string;
	closingChecklist: string;
	isClearedForOperationDetails: IsClearedForOperationDetails;
	locationProperties: string[];
	language?: string;
	noteTypes: NoteTypeReference[];
	noteTemplates: NoteTemplateReference[];
	downtimeTemplates: DowntimeTemplateReference[];
	lastServerUpdate: string;
	lastUpdateFailed: boolean;
	lastQueueUpdate: LastQueueTimeUpdate;
	lastRidersUpdate: LastRidersUpdate;
	riders: number;
	dispatches: number;
	// Carousel pointer for the asset dispatch queue widget. Points at the "in action" unit.
	// Advances by one on each real per-unit asset dispatch and is persisted so the position
	// survives app reloads.
	assetDispatchQueueIndex: number;
	// Occupied-seat (rider) count of the most recent per-unit asset dispatch. Persisted alongside
	// the queue index so the "Dispatched" card can show how many riders were sent on the unit that
	// was just dispatched. Undefined until the first asset dispatch of the run.
	lastAssetDispatchOccupiedSeats?: number;
	// Occupied-seat count of the most recent asset dispatch, keyed by unit id. Lets the dispatch
	// widget pre-fill the number-selector with a unit's own last dispatched count when it returns
	// to the "in action" position, instead of resetting to 0. Persisted alongside the queue index
	// so it survives reloads. Empty until the first asset dispatch of the run.
	lastAssetDispatchCountByUnitId: Record<string, number>;
	// Start of the current location-open session (ISO timestamp), set when the operator opens the location.
	// Device-local: cleared on location switch; kept across close so same-day reopen is not "first open".
	// Used by the units-in-use prompt (first open / new location). Persisted across reloads.
	locationOpenSessionStart?: string;
	// Wall-clock unlock time for the minimum-seconds-between-dispatches hold. Persisted so navigating
	// away (or reloading) cannot bypass the configured timeout.
	dispatchCooldownUntil?: string;
	// Operator-defined unit dispatch order per location, persisted on device (localStorage). Key is
	// location id; value is ordered unit ids. Survives app reload and close/reopen for the same location.
	assetUnitOrderByLocationId: Record<string, string[]>;
	lastExternalDispatch?: LastExternalDispatch;
	lastExternalQueueTime?: LastExternalQueueTime;
	openingTime?: string;
	closingTime?: string;
	stateLastUpdated?: string;
	organizationDateTimeSettings?: DateTimeSettings;
}

export interface IsClearedForOperationDetails {
	isClearedForPreopening: boolean;
	isClearedForOperation: boolean;
	hasBlockingDowntime: boolean;

	assignments: {
		id: string;
		name: string;
		description: string;
		assignees: DocumentReference[];
		created: string;
		end: string;
		priority: boolean;
	}[];

	checklists: {
		id: string;
		name: string;
		asset?: string;
		due?: string;
		end: string;
	}[];

	preopeningChecklists: PreopeningChecklist[];

	noteHandoversRequiringAcknowledgement: DocumentReference[];
}

export interface PreopeningChecklist {
	id: string;
	name: string;
	requiresApproval: boolean;

	completed: string;
	approved: string;
	expires: string;

	isCleared: boolean;
}

export interface RidePosition {
	identifier: number;
	name: string;
	mandatory: boolean;
	description: string;
	requiredCompetencies: DocumentReference[];
	attendant?: RidePositionAttendant;
}

export interface RidePositionAttendant {
	loggedIn: string;
	user: {
		id: string;
		name: string;
		profileImage: string;
	};
}

export interface LastRidersUpdate {
	riders: number;
	lastUpdated: Date | string;
}

export interface LastExternalDispatch {
	lastUpdated: Date | string;
	timestamp: Date | string;
	riders: number;
	utilization: number;
}

export interface LastExternalQueueTime {
	lastUpdated: Date | string;
	timestamp: Date | string;
	queueTimeInMinutes: number;
	queueSize: number;
}

export interface ExternalDataSettings {
	safariAiEnabled: boolean;
}

export interface BulletinWidgetSettings {
	bulletinWidgetEnabled: boolean;
	title: string;
	content: string;
}

export interface LastQueueTimeUpdate {
	time: number;
	lastUpdated: Date | string;
}

export interface Downtime {
	description: string;
	id: string;
	state: "Open" | "Blocking";
	updated: string;
}

export interface NoteTypeReference extends DocumentReference {
	isDefault: boolean;
	isDeleted: boolean;
}

export interface NoteTemplateReference extends DocumentReference {
	color: string;
	noteType: string;
	title: string;
	description: string;
	name: string;
}

export interface DowntimeTemplateReference extends DocumentReference {
	description: string;
	color: string;
	categories: string[];
	assignmentConfigurationPolicyType?: "Inherit" | "Create" | "DoNotCreate";
}

export interface Location extends DocumentReference {
	language: string;
	operators?: string[];
	scannerCode?: string;
	barcodeSignIn?: boolean;
}

export interface OperationalDispatchUnit {
	id: string;
	name: string;
	sortOrder: number;
	capacity: number;
}

export interface RideOpsSettings {
	dispatchUnitCapacity: number;
	dispatchUnits: number;
	useAssetBasedDispatchConfiguration: boolean;
	units: OperationalDispatchUnit[];
	allowStatisticsForOperator: boolean;
	dispatchUpdateWarningThreshold: number;
	queueTimeUpdateWarningThreshold: number;
	maximumQueueTimeMinutes: number;
	queueTimeStepSizeMinutes: number;
	lockDeviceSettings: boolean;
	minumumSecondsBetweenDispatches: number;
	messageForOperatorWhenNotReadyForOperation: string;
	messageForOperatorWhenNoCriticalForOperationChecklistsPlanned: string;
	positions: RidePosition[];
	attendants: Operator[];
	signoutAllAttendantsOnDowntimeCreation: boolean;
	externalDataSettings: ExternalDataSettings;
	bulletinWidgetSettings: BulletinWidgetSettings;
	delayedDispatches: boolean;
	emptyDispatches: boolean;
	closingTimeReminderEnabled: boolean;
	allowRideOpsOperatorToResolveNonBlockingDowntime: boolean;
}
